import { inject, Injectable } from '@angular/core';
import { ConfigBaseFilter, ListLayout, ListLayoutMultipleComponent, SimpleConfigBaseFilter } from 'gung-list';
import { MetadataService, Product, ProductArticleClassFilterService, SelectedCustomerService } from 'gung-standard';
import { JeevesProductListConfigService } from 'gung-standard-jeeves';
import { environment } from 'projects/car-liner/src/environments/environment';
import { defer, first, forkJoin, iif, map, Observable, of, switchMap } from 'rxjs';
import { CarLinerProductGridViewComponent } from '../components/car-liner-product-grid-view/car-liner-product-grid-view.component';
import { CarLinerProductTableComponent } from '../components/car-liner-product-table/car-liner-product-table.component';

export class ArticleTypeFilter extends SimpleConfigBaseFilter<Product> {
  constructor(protected metadataService: MetadataService) {
    super();
  }

  getName(): string {
    return 'ARTICLETYPE';
  }

  getOptionIds(item: Product): string[] {
    if (item?.extra?.ar?.itemtypecd1) {
      return [item.extra.ar.itemtypecd1];
    }
    return [];
  }

  getOptionName(key: string): string {
    return this.metadataService.getMetadataValue('atp1', 'itemtypedesc', key);
  }
}

export class OemFilter extends SimpleConfigBaseFilter<Product> {
  getName(): string {
    return 'OEM';
  }

  getOptionIds(item: Product): string[] {
    if (item?.extra?.pim?.josamOEM && environment.projectName === 'josam') {
      const oem = item.extra.pim.josamOEM as string;
      return oem.split(',');
    }else if(item?.extra?.pim?.blackhawkOEM && environment.projectName === 'blackhawk'){
      const oem = item.extra.pim.blackhawkOEM as string;
      return oem.split(',');
    }else if(item?.extra?.pim?.OEM && environment.projectName !== 'josam' && environment.projectName !== 'blackhawk'){
      const oem = item.extra.pim.OEM as string;
      return oem.split(',');
    }
    return [];
  }
  
  getOptionName(key: string): string {
    return key;
  }
}

export class CarLinerProductBlad extends SimpleConfigBaseFilter<Product> {
  constructor(protected metadataService: MetadataService) {
    super();
  }

  getName(): string {
    return 'Q_PRODUKTBLAD';
  }

  getOptionIds(item: Product): string[] {
    if (item?.extra?.ar?.q_prodblad) {
      return [item.extra.ar.q_prodblad];
    }
    return [];
  }

  getOptionName(key: string): string {
    return this.metadataService.getMetadataValue('q_produktblad', 'q_beskr', key);
  }
}

export class PimIsInNewsFilter extends SimpleConfigBaseFilter<Product> {
  isActiveFromStart: () => true;
  isJosamProject: boolean;

  constructor(isJosamProject: boolean) {
    super();
    this.isJosamProject = isJosamProject;
  }

  getName(): string {
    return 'Miscellaneous';
  }

  getOptionIds(item: Product): string[] {
    const options = [];
    if (item.extra?.pim?.isJosamNews && environment.projectName === 'josam') {
      options.push('SHOW_NEW_PRODUCTS');
    }else if(item.extra?.pim?.isBlackhawkNews && environment.projectName === 'blackhawk'){
      options.push('SHOW_NEW_PRODUCTS');
    }else if(item.extra?.pim?.isNews && environment.projectName !== 'josam' && environment.projectName !== 'blackhawk') {
      options.push('SHOW_NEW_PRODUCTS');
    }
    
    if (item.extra?.pim?.isCatalogSelection) {
      options.push('SHOW_CATALOG_SELECTION');
    }

    if (item.extra?.ar?.itemstatuscode === 8) {
      options.push('TO_BE_PHASED_OUT');
    }

    if (item.extra?.campaign) {
      options.push('SHOW_CAMPAIGN');
    }

    return options;
  }

  getOptionName(key: string): string {
    return key;
  }
}

class CarLinerProductArticleClassFilterService extends ProductArticleClassFilterService {
  getOptionIds(item: Product): string[] {
    const id = item.extra?.ar?.artprodkonto;
    return [id];
  }
  getOptionName(key: string): string {
    const artprodkontobeskr = this.metadataService.getMetadataValue('arpk', 'artprodkontobeskr', key);
    return artprodkontobeskr || key;
  }
}

@Injectable({
  providedIn: 'root'
})
export class CarLinerProductListConfigService extends JeevesProductListConfigService {
  private metadataService = inject(MetadataService);
  private selectedCustomerService = inject(SelectedCustomerService);

  isJosamProject = environment.projectName === 'josam' ? true : false;
  isBlackhawkProject = environment.projectName === 'blackhawk' ? true : false;

  getFilters(): ConfigBaseFilter<Product>[] {
    let filters = [
      new PimIsInNewsFilter(this.isJosamProject),
      new CarLinerProductArticleClassFilterService(this.metadataService),
      this.productItemGroupFilterService,
      new ArticleTypeFilter(this.metadataService),
      new OemFilter()
    ];
    if (this.isJosamProject) {
      filters.push(new CarLinerProductBlad(this.metadataService));
    }
    return filters;
  }

  getLayouts(): ListLayout<Product>[] {
    return [
      {
        getIconClass: () => 'fa fa-th',
        getListItemComponent: () => CarLinerProductGridViewComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'ProductGrid'
      },
      {
        getIconClass: () => 'fa fa-list',
        getListItemComponent: () => CarLinerProductTableComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'ProductList'
      }
    ];
  }

  getItems(): Observable<Product[]> {
    return this.selectedCustomerService.getSelectedCustomer().pipe(
      switchMap(customer =>
        forkJoin([
          iif(
            () => this.isBlackhawkProject, // Lazy condition
            defer(() => this.productService.getProductsByAssortment('BLACKHAWK_' + customer.extra.kus.prislista).pipe(first())),
            defer(() => this.productService.getProductsByAssortment(customer.extra.kus.prislista).pipe(first()))
          ),
          this.getCampaignArticleIds(customer.extra.kus.prislista)
        ])
      ),
      map(([products, campaignIds]) => {
        products.forEach(p => (p.extra.campaign = campaignIds.has(p.id)));
        return this.sortProducts(products);
      })
    );
  }

  // Article ids on the active campaign for this pricelist (empty when no active campaign).
  private getCampaignArticleIds(pricelistId: string): Observable<Set<string>> {
    const prh = this.metadataService.getMetaDataTableMap('prh')?.[String(pricelistId)];
    if (!prh?.kampanjprislista) {
      return of(new Set<string>());
    }
    const today = new Date();
    const active = new Date(prh.kampanjfromdat) <= today && today <= new Date(prh.kampanjtomdat);
    if (!active) {
      return of(new Set<string>());
    }
    return this.productService
      .getProductsByAssortment(prh.kampanjprislista)
      .pipe(first(), map(campaignProducts => new Set(campaignProducts.map(p => p.id))));
  }

  private getSequenceKey(): string {
    if (this.isJosamProject) return 'josamSequence';
    if (this.isBlackhawkProject) return 'blackhawkSequence';
    return 'sequence';
  }

  private sortProducts(products: Product[]): Product[] {
    const key = this.getSequenceKey();
  
    return products.sort((a, b) => {
      const aVal = Number(a.extra?.pim?.[key] ?? null);
      const bVal = Number(b.extra?.pim?.[key] ?? null);
  
      if (isNaN(aVal) && isNaN(bVal)) return 0;
      if (!isNaN(aVal) && isNaN(bVal)) return -1;
      if (isNaN(aVal) && !isNaN(bVal)) return 1;
  
      return bVal - aVal;
    });
  }

  getSearchTerms(item: Product) {
    const artbeskr = [item.extra?.ar?.artbeskr_original];
    if (item.extra?.i18n) {
      const langs = Object.keys(item.extra?.i18n);
      artbeskr.push(...langs.map(lang => item.extra?.i18n[lang].artbeskr).filter(a => !!a));
    }
    return [item.id, item.name, item.extra?.ar?.q_popnr, item.extra.ar?.artnrean, ...artbeskr, item.extra?.ar?.ersatterartnr]; 
  }
}
